import React, { memo, useMemo, useRef, useEffect, useCallback } from "react";
import { Container, useApp } from "@pixi/react";
import SpineTextureExtractor from "../../utils/SpineTextureExtractor";
import TickerTimeout from "../../utils/TickerTimeout";
import HoldAndWinCell from "./HoldAndWinCell";
import { jackpotCoinSkin } from "./jackpotCoin";
import {
  BLANK_SYMBOL_ID,
  COIN_SYMBOL_ID,
  COIN_JACKPOT_SYMBOL_ID,
  CHEST_SYMBOL_ID,
  GOLDEN_CHEST_SYMBOL_ID,
  SCARAB_SYMBOL_ID,
} from "../../SymbolData";

/**
 * HoldAndWinBoard — the Cleopatra's Fire Hold & Win grid, 5 x 8.
 *
 * Forty HoldAndWinCells, each spinning on its own (HoldAndWinCellController).
 * Centred on the room's reel slot, whose bone sits on the centre of the 5 x 8
 * cell grid drawn into the frame. `cellWidth` / `cellHeight` are one cell in
 * container pixels; the art is fitted to the cell height, so the 170 x 139
 * cells keep square symbols.
 *
 * Cells are addressed [column][row], row 0 = the TOP screen row.
 *
 * `overlayRef` — the HoldAndWinOverlay in the room's front slot. A cell that
 * lands on anything but a blank is handed to it: the overlay plays the
 * symbol's spine (in -> idle) and the cell's static picture is hidden.
 *
 * `devSpinLoop` — TEMPORARY, for looking at the room: spins every cell,
 * stops them one by one on random symbols, and repeats. No state, no server.
 *
 * `kinds` / `values` — the server's grid (holdAndWin slice, SCREEN rows,
 * [column][row]). Drawn as it stands whenever the dev loop is off: every
 * non-empty cell plays its spine on the overlay, the rest stay empty. NOT
 * while a respin is in flight — the respin lands its own cells.
 *
 * `controlRef` — receives the respin handle HoldAndWinControlContext drives:
 *   spinEmpty(kinds)             spin every empty cell, returns their [c, r]
 *   land(cells, kinds, values)   stop them in order onto the server's grid;
 *                                resolves when the last one has landed
 *   quickStop()                  land everything still turning, now
 *   freeze()                     stop dead where they are (a refused respin)
 */
export const HOLD_AND_WIN_COLUMNS = 5;
export const HOLD_AND_WIN_ROWS = 8;

// What the dev loop lands on: mostly blanks, some coins, a chest, a scarab.
const DEV_LANDING_POOL = [
  BLANK_SYMBOL_ID,
  BLANK_SYMBOL_ID,
  BLANK_SYMBOL_ID,
  BLANK_SYMBOL_ID,
  9, // coin
  9,
  12, // chest
  14, // scarab
];
const DEV_COIN_VALUES = [20, 40, 60, 100, 200];
const DEV_SPIN_MS = 600; // spin before the first cell stops
const DEV_STOP_GAP_MS = 40; // between one cell's stop and the next
const DEV_REST_MS = 1500; // landed board on screen before the next spin

// bonus_data.cell_kinds -> the grid sprite (contract §6). `pending_chest` is a
// chest waiting for its row; it draws as a chest.
const KIND_TO_SYMBOL = {
  coin: COIN_SYMBOL_ID,
  chest: CHEST_SYMBOL_ID,
  pending_chest: CHEST_SYMBOL_ID,
  golden_chest: GOLDEN_CHEST_SYMBOL_ID,
  scarab: SCARAB_SYMBOL_ID,
};

/**
 * A cell's symbol: the kind's sprite — except a coin whose points the math
 * makes a jackpot, which is `cj` with that jackpot's skin (jackpotCoin.js).
 */
const resolveCell = (kind, points, jackpots) => {
  const id = KIND_TO_SYMBOL[kind];
  if (id === COIN_SYMBOL_ID) {
    const skin = jackpotCoinSkin(points, jackpots);
    if (skin) return { id: COIN_JACKPOT_SYMBOL_ID, skin };
  }
  return { id, skin: null };
};

const HoldAndWinBoard = memo(({
  cellWidth,
  cellHeight,
  overlayRef,
  devSpinLoop = false,
  kinds = null,
  values = null,
  points = null,
  jackpots = null,
  controlRef = null,
}) => {
  const app = useApp();
  const cellRefs = useRef(
    Array.from({ length: HOLD_AND_WIN_COLUMNS }, () =>
      Array.from({ length: HOLD_AND_WIN_ROWS }, () => React.createRef()),
    ),
  );

  // ─── Respin state ───
  // busy: a respin owns the board (spinning or landing) — the grid effect
  // below keeps its hands off. pending: cells still to land this respin.
  // stops: the scheduled per-cell stops, cleared by a quick stop.
  const respin = useRef({ busy: false, pending: new Set(), resolve: null, stops: [], queue: [] });
  // The jackpot skin each landing cell is waiting to show ("c_r" -> skin).
  const landingSkins = useRef(new Map());

  // Landed: hand the cell to its overlay spine. Stable, so cells never
  // re-render for it.
  const handleLanded = useCallback(
    (col, row, symbolId, value) => {
      const skin = landingSkins.current.get(`${col}_${row}`) ?? null;
      landingSkins.current.delete(`${col}_${row}`);
      const played = overlayRef?.current?.play(col, row, symbolId, value, skin);
      if (played) cellRefs.current[col][row]?.current?.hide();
      else overlayRef?.current?.clear(col, row);

      const r = respin.current;
      // The owner hears each cell AS it lands (the respin counter resets on
      // the first qualifying landing, not after the whole respin).
      r.onLanded?.(col, row, symbolId);
      if (r.pending.delete(`${col}_${row}`) && r.pending.size === 0) {
        r.busy = false;
        const resolve = r.resolve;
        r.resolve = null;
        resolve?.();
      }
    },
    [overlayRef],
  );

  // ─── The respin handle ───
  useEffect(() => {
    if (!controlRef) return undefined;
    const cellAt = (c, r) => cellRefs.current[c]?.[r]?.current;
    const clearStops = () => {
      respin.current.stops.forEach((t) => t?.clear?.());
      respin.current.stops = [];
    };

    const api = {
      spinEmpty(currentKinds) {
        const cells = [];
        // Column by column, top to bottom — the order the cells will stop in.
        for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
          for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
            if (KIND_TO_SYMBOL[currentKinds?.[c]?.[r]] !== undefined) continue;
            const cell = cellAt(c, r);
            if (!cell) continue;
            overlayRef?.current?.clear(c, r);
            cell.spin();
            cells.push([c, r]);
          }
        }
        respin.current.busy = cells.length > 0;
        return cells;
      },

      land(
        cells,
        nextKinds,
        nextValues,
        gapMs = 0,
        nextPoints = null,
        nextJackpots = null,
        onLanded = null,
      ) {
        const r = respin.current;
        r.onLanded = onLanded;
        clearStops();
        if (!cells.length) {
          r.busy = false;
          return Promise.resolve();
        }
        r.pending = new Set(cells.map(([c, row]) => `${c}_${row}`));
        r.queue = cells.slice();
        const done = new Promise((resolve) => {
          r.resolve = resolve;
        });
        // Every target first, then the stops one by one.
        cells.forEach(([c, row]) => {
          const cell = resolveCell(
            nextKinds?.[c]?.[row],
            nextPoints?.[c]?.[row],
            nextJackpots,
          );
          const id = cell.id ?? BLANK_SYMBOL_ID;
          const value = id === BLANK_SYMBOL_ID ? null : nextValues?.[c]?.[row] || null;
          if (cell.skin) landingSkins.current.set(`${c}_${row}`, cell.skin);
          cellAt(c, row)?.setLanding(id, value);
        });
        cells.forEach(([c, row], i) => {
          r.stops.push(
            new TickerTimeout(() => {
              r.queue = r.queue.filter(([qc, qr]) => qc !== c || qr !== row);
              cellAt(c, row)?.stop(false);
            }, i * gapMs),
          );
        });
        return done;
      },

      quickStop() {
        const r = respin.current;
        const left = r.queue;
        clearStops();
        r.queue = [];
        left.forEach(([c, row]) => cellAt(c, row)?.stop(true));
      },

      /** The live-spine layer over the board (chest collections). */
      overlay() {
        return overlayRef?.current ?? null;
      },

      freeze() {
        const r = respin.current;
        clearStops();
        r.queue = [];
        for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
          for (let row = 0; row < HOLD_AND_WIN_ROWS; row++) cellAt(c, row)?.freeze();
        }
        r.pending.clear();
        r.busy = false;
        r.resolve = null;
      },
    };

    controlRef.current = api;
    return () => {
      clearStops();
      if (controlRef.current === api) controlRef.current = null;
    };
  }, [controlRef, overlayRef]);

  const spritesheet = useMemo(() => {
    if (app?.renderer) SpineTextureExtractor.initialize(app.renderer);
    return SpineTextureExtractor.getSymbols();
  }, [app?.renderer]);

  // ─── Dev spin loop ───
  useEffect(() => {
    if (!devSpinLoop || !spritesheet?.textures) return undefined;

    let cancelled = false;
    const timers = [];
    const after = (ms, fn) => {
      timers.push(
        new TickerTimeout(() => {
          if (!cancelled) fn();
        }, ms),
      );
    };
    const eachCell = (fn) => {
      for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
        for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
          const cell = cellRefs.current[c][r]?.current;
          if (cell) fn(cell);
        }
      }
    };

    const round = () => {
      overlayRef?.current?.clearAll();
      eachCell((cell) => cell.spin());

      let order = 0;
      eachCell((cell) => {
        const id =
          DEV_LANDING_POOL[Math.floor(Math.random() * DEV_LANDING_POOL.length)];
        const value =
          id === 9
            ? DEV_COIN_VALUES[Math.floor(Math.random() * DEV_COIN_VALUES.length)]
            : null;
        cell.setLanding(id, value);
        after(DEV_SPIN_MS + order * DEV_STOP_GAP_MS, () => cell.stop(false));
        order += 1;
      });

      after(DEV_SPIN_MS + order * DEV_STOP_GAP_MS + DEV_REST_MS, round);
    };

    // Give the cells a frame to build their controllers.
    after(500, round);

    return () => {
      cancelled = true;
      timers.forEach((t) => t?.clear?.());
    };
  }, [devSpinLoop, spritesheet, overlayRef]);

  // ─── The server's grid ───
  // A frame after mount, so the cells have built their controllers.
  useEffect(() => {
    if (devSpinLoop || !kinds || !spritesheet?.textures) return undefined;
    if (respin.current.busy) return undefined;
    const timer = new TickerTimeout(() => {
      if (respin.current.busy) return;
      for (let c = 0; c < HOLD_AND_WIN_COLUMNS; c++) {
        for (let r = 0; r < HOLD_AND_WIN_ROWS; r++) {
          const cell = cellRefs.current[c][r]?.current;
          const { id, skin } = resolveCell(kinds[c]?.[r], points?.[c]?.[r], jackpots);
          const value = values?.[c]?.[r] || null;
          if (id !== undefined && overlayRef?.current?.play(c, r, id, value, skin)) {
            cell?.hide();
          } else {
            overlayRef?.current?.clear(c, r);
            cell?.show(BLANK_SYMBOL_ID);
          }
        }
      }
    }, 50);
    return () => timer.clear();
  }, [devSpinLoop, kinds, values, points, jackpots, spritesheet, overlayRef]);

  if (!spritesheet?.textures || !cellWidth || !cellHeight) return null;

  const left = -((HOLD_AND_WIN_COLUMNS - 1) / 2) * cellWidth;
  const top = -((HOLD_AND_WIN_ROWS - 1) / 2) * cellHeight;

  return (
    <Container name="hold_and_win_board">
      {Array.from({ length: HOLD_AND_WIN_COLUMNS }, (_, c) =>
        Array.from({ length: HOLD_AND_WIN_ROWS }, (_, r) => (
          <Container
            key={`hnw_${c}_${r}`}
            x={left + c * cellWidth}
            y={top + r * cellHeight}
          >
            <HoldAndWinCell
              ref={cellRefs.current[c][r]}
              width={cellWidth}
              height={cellHeight}
              reelIndex={c}
              rowIndex={r}
              symbolId={BLANK_SYMBOL_ID}
              spritesheet={spritesheet}
              onLanded={handleLanded}
            />
          </Container>
        )),
      )}
    </Container>
  );
});

HoldAndWinBoard.displayName = "HoldAndWinBoard";

export default HoldAndWinBoard;
